import type { FC, ReactNode } from 'react';

export interface StatTileProps {
  label: string;
  /** Pre-formatted — the tile never does arithmetic. */
  value: string;
  hint?: string;
  /** Decorative only; the label carries the identity. */
  icon?: ReactNode;
  loading?: boolean;
  /**
   * 0..100 renders a meter beneath the figure; null renders nothing. The figure
   * itself always states the value, so the meter is never its sole carrier.
   */
  meterPercent?: number | null;
}

export const StatTile: FC<StatTileProps> = ({
  label,
  value,
  hint,
  icon,
  loading = false,
  meterPercent = null,
}) => (
  <div className="dashboard-tile">
    <div className="dashboard-tile__head">
      <span className="dashboard-tile__label">{label}</span>
      {icon && (
        <span className="dashboard-tile__icon" aria-hidden="true">
          {icon}
        </span>
      )}
    </div>

    {loading ? (
      <div
        className="dashboard-card__placeholder dashboard-tile__placeholder"
        aria-hidden="true"
      />
    ) : (
      <span className="dashboard-tile__value">{value}</span>
    )}

    {/*
      Purely a visual echo of the figure above it, so it is hidden from
      assistive tech rather than announced a second time.
    */}
    {!loading && meterPercent !== null && (
      <div className="dashboard-meter dashboard-meter--compact" aria-hidden="true">
        <div
          className="dashboard-meter__fill"
          style={{ width: `${Math.min(Math.max(meterPercent, 0), 100)}%` }}
        />
      </div>
    )}

    {!loading && hint && <span className="dashboard-tile__hint">{hint}</span>}
  </div>
);
